/**
 * PriceBlock — canonical customer-facing price display for IJE HAUS.
 *
 * Rules (from spec):
 * - Guest / not logged in → show MSRP + Wholesale, no strikethrough on Wholesale, no "Your Price"
 * - Approved customer with discount = 0 → same as guest (no strikethrough)
 * - Approved customer with discount > 0 → show MSRP, Wholesale (strikethrough), Your Price (highlight)
 *   plus a "You save X%" badge next to Your Price.
 *
 * Net price = wholesale × (1 - discount_pct / 100), rounded to 2 decimals.
 * The DJ `your_price` / `your_discount_pct` fields are stripped server-side and must never appear.
 */
import { Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

function fmt(n) {
  return Number(n || 0).toFixed(2);
}

export default function PriceBlock({ product, size = 'default' }) {
  const locked = product.pricing_locked;
  const msrp = Number(product.msrp || 0);
  const wholesale = Number(product.wholesale_price || 0);
  const discount = Number(product.account_discount_percent || 0);
  const net = discount > 0 ? Math.round(wholesale * (1 - discount / 100) * 100) / 100 : wholesale;
  const hasDiscount = discount > 0 && wholesale > 0;
  // NeuroSource lists MSRP per single retail unit, but buyers order full POP
  // cases — surface the per-case number too so they don't have to do the math.
  const showCaseMsrp = product.source === 'neurosource' && msrp > 0;
  const casePack = Number(product.case_pack_size || 0);
  const msrpCase = showCaseMsrp && casePack ? msrp * casePack : 0;

  const scale = size === 'compact' ? 'text-base' : size === 'card' ? 'text-lg' : size === 'lg' ? 'text-4xl' : 'text-2xl';

  if (locked) {
    return (
      <div className="space-y-1">
        <p className="sans text-xs text-neutral-500 tracking-widest uppercase">
          MSRP <span className="text-neutral-700">${fmt(msrp)}{showCaseMsrp ? '/unit' : ''}</span>
          {showCaseMsrp && (
            <span className="text-neutral-500"> · ${fmt(msrpCase)}<span className="text-neutral-400">/case</span></span>
          )}
        </p>
        <p className="sans text-xs uppercase tracking-[0.2em] flex items-center gap-1.5" style={{ color: 'var(--ije-pink)' }}>
          <Lock size={12} strokeWidth={1.6} />
          <Link to="/login" className="underline decoration-transparent hover:decoration-current">Sign in for wholesale</Link>
        </p>
      </div>
    );
  }

  if (size === 'compact') {
    // Two-line dense variant for the mobile compact catalog grid.
    return (
      <div className="space-y-0.5" data-testid="price-compact">
        <p className="sans text-[10px] text-neutral-500 tracking-widest uppercase" data-testid="price-msrp">
          MSRP <span className="text-neutral-700">${fmt(msrp)}{showCaseMsrp ? '/u' : ''}</span>
          {showCaseMsrp && (
            <span className="text-neutral-500"> · ${fmt(msrpCase)}/case</span>
          )}
        </p>
        <p className="sans text-xs flex items-baseline gap-1.5" data-testid="price-wholesale">
          {hasDiscount ? (
            <>
              <span className="line-through text-neutral-500">${fmt(wholesale)}</span>
              <span className="serif text-base pink-gradient-text">${fmt(net)}</span>
            </>
          ) : (
            <span className="serif text-base" style={{ color: 'var(--ije-black)' }}>${fmt(wholesale)}</span>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* MSRP always visible, small + gray. For NeuroSource we show both
          per-unit and per-case (per-case = msrp × case_pack_size). */}
      <p className="sans text-xs text-neutral-500 tracking-widest uppercase" data-testid="price-msrp">
        MSRP <span className="text-neutral-700">${fmt(msrp)}{showCaseMsrp ? ' /unit' : ''}</span>
        {showCaseMsrp && (
          <span className="text-neutral-500"> · <span className="text-neutral-700">${fmt(msrpCase)}</span> /case of {casePack}</span>
        )}
      </p>

      {/* Wholesale — crossed-out ONLY when there's a discount */}
      <p className="sans text-sm" data-testid="price-wholesale">
        <span className="text-neutral-500 tracking-widest uppercase text-xs mr-2">Wholesale</span>
        {hasDiscount ? (
          <span className="line-through text-neutral-500">${fmt(wholesale)}</span>
        ) : (
          <span className={`serif ${scale}`} style={{ color: 'var(--ije-black)' }}>${fmt(wholesale)}</span>
        )}
      </p>

      {/* Your Price — only when discount applies */}
      {hasDiscount && (
        <div className="flex items-baseline gap-3 pt-1" data-testid="price-your">
          <span className="sans text-xs text-neutral-500 tracking-widest uppercase">Your Price</span>
          <span className={`serif ${scale} pink-gradient-text`}>${fmt(net)}</span>
          <span
            className="sans text-[0.65rem] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full"
            style={{ background: 'var(--ije-pink-tint)', color: 'var(--ije-pink-dark)' }}
            data-testid="price-savings-badge"
          >
            You save {discount}%
          </span>
        </div>
      )}
    </div>
  );
}
